import { z } from 'zod'

import { messages } from '@/lib/messages'
import type { CashItemPlanWrite, CashPlanItem } from '@/services/http/cash'

const t = messages.cash.planning

/** Tutar metni: "1.234,50" (Türkçe) ya da "1234.5" (sunucudan gelen); sayı değilse `NaN`. */
export function parseAmount(text: string): number {
  const trimmed = text.trim()
  if (!trimmed) return Number.NaN
  const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed
  return Number(normalized)
}

/**
 * Planlama kalemi editörünün form değerleri. Alan adları `CashItemPlanWrite` ile aynıdır, böylece
 * sunucunun 422 yolu (`parts.0.amount`, `note`) eşleme olmadan alana düşer.
 */
export const itemFormSchema = z.object({
  parts: z.array(
    z.object({
      date: z.string(),
      amount: z.string().refine((value) => parseAmount(value) > 0, t.amountInvalid),
      instrument: z.string(),
    }),
  ),
  remainder_date: z.string(),
  remainder_instrument: z.string(),
  klass: z.string(),
  erp_outside: z.boolean(),
  note: z.string().max(1000, t.noteTooLong),
})

export type ItemFormValues = z.infer<typeof itemFormSchema>

export function itemFormValues(item: CashPlanItem): ItemFormValues {
  return {
    parts: item.parts.map((part) => ({
      date: part.date ?? '',
      amount: String(part.amount),
      instrument: part.instrument ?? 'Banka',
    })),
    remainder_date: item.remainder_date_manual ?? '',
    remainder_instrument: item.remainder_instrument ?? 'Banka',
    klass: item.klass,
    erp_outside: item.erp_outside,
    note: item.note ?? '',
  }
}

/** Kaydet gövdesi; varsayılan "Banka" kalan aracı, kalemde araç yoksa boş gönderilir. */
export function itemPlanBody(
  firm: string,
  item: CashPlanItem,
  values: ItemFormValues,
  version: number,
): CashItemPlanWrite {
  return {
    firm,
    key: item.key,
    klass: values.klass as CashItemPlanWrite['klass'],
    erp_outside: values.erp_outside,
    parts: values.parts.map((part) => ({
      date: part.date || null,
      amount: parseAmount(part.amount),
      instrument: part.instrument,
    })),
    remainder_date: values.remainder_date || null,
    remainder_instrument:
      values.remainder_instrument === 'Banka' && !item.remainder_instrument
        ? null
        : values.remainder_instrument,
    note: values.note || null,
    version,
  }
}
